
import { SupportedLanguage } from '@/types/language';

// Import translations directly
import en from '@/locales/en/common.json';
import zhTW from '@/locales/zh-TW/common.json';

// Create a mapping of language files
const languageFiles = {
  'en': en,
  'zh-TW': zhTW
};

// Helper function to get a value from a nested object using dot notation
const getNestedValue = (obj: any, path: string): string | null => {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = current[key];
  }
  
  return typeof current === 'string' ? current : null;
};

// Custom translations that can be used when the key is not in the translation files
const customTranslations: Record<string, Record<string, string>> = {
  'en': {
    'filter': 'Filter',
    'clear': 'Clear',
    'easyApply': 'Easy Apply',
    'flexibleContract': 'Flexible Contract',
    'reviews.search': 'Search',
    'reviews.searchAddress': 'Enter location',
    'bedrooms': 'Bedrooms',
    'bathrooms': 'Bathrooms',
    'singleReview': 'Single Review',
    'reviews.lastReview': 'Last review',
    'form.landlordName': 'Landlord or Community Name',
    'rentalStatus.viewed': 'Viewed',
    'rentalStatus.renting': 'Currently Renting',
    'rentalStatus.ended': 'Rental Ended',
    'gender': 'Gender',
    'nationality': 'Nationality',
    'reviews.months': 'Months',
    'reviews.noticePeriod': 'Notice Period',
    'reviews.foundAddresses': 'Found {{count}} addresses ({{reviews}} reviews)',
    'reviews.viewAsList': 'View as list',
    'latestReview': 'Latest review',
    // Parking translations
    'parking.paid_indoor': 'Paid Indoor Parking',
    'parking.free_indoor': 'Free Indoor Parking',
    'parking.paid_outdoor': 'Paid Outdoor Parking',
    'parking.free_outdoor': 'Free Outdoor Parking',
    'parking.street_parking': 'Street Parking',
    'parking.garage': 'Garage',
    'parking.driveway': 'Driveway',
    // Authentication and verification
    'auth.forgotPassword': 'Forgot Password',
    'auth.forgotPasswordDesc': 'Enter your email address and we\'ll send you a link to reset your password.',
    'auth.passwordResetEmailSent': 'Password Reset Email Sent',
    'auth.checkEmailForPasswordReset': 'Please check your email for the password reset link.',
    'auth.sendResetLink': 'Send Reset Link',
    'auth.sending': 'Sending...',
    'auth.invalidEmail': 'Invalid Email',
    'auth.pleaseEnterValidEmail': 'Please enter a valid email address.',
    'auth.passwordResetFailed': 'Password Reset Failed',
    'auth.resetYourPassword': 'Reset Your Password',
    'auth.enterNewPassword': 'Please enter a new password for your account.',
    'auth.newPassword': 'New Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.passwordResetSuccess': 'Password Reset Successful',
    'auth.passwordResetSuccessDesc': 'Your password has been successfully reset.',
    'auth.passwordTooShort': 'Password must be at least 6 characters long.',
    'auth.passwordsDontMatch': 'Passwords do not match.',
    'auth.resetPassword': 'Reset Password',
    'auth.updating': 'Updating...',
    'auth.invalidOrExpiredResetLink': 'Invalid or expired reset link.',
    'auth.requestNewResetLinkDesc': 'Please request a new password reset link.',
    'auth.backToLogin': 'Back to Login',
    'auth.verifyingResetLink': 'Verifying reset link...',
    'auth.emailNotVerified': 'Your email is not yet verified. Please check your inbox for a verification email.',
    'auth.resendVerificationEmail': 'Resend Verification Email',
    'auth.verificationEmailSent': 'Verification Email Sent',
    'auth.checkEmailForVerification': 'Please check your email for the verification link.',
    'auth.verificationEmailFailed': 'Failed to Send Verification Email',
    'auth.checkYourEmail': 'Check your email',
    'auth.verificationEmailSentDesc': 'We have sent a verification email to your inbox. Please click the link in the email to verify your account.',
    'auth.didntReceiveEmail': "Didn't receive the email?",
    'auth.completeProfile': 'Complete Your Profile',
    'auth.pleaseFillProfile': 'Please fill in your information to complete registration',
    'auth.emailVerifyFailed': 'Email verification failed',
    'auth.email': 'Email',
    'auth.enterYourEmail': 'Enter your email address',
    // Rate limiting translations
    'auth.dailyLimitReached': 'You have reached the daily limit of email sending. Please try again tomorrow.',
    'auth.pleaseWaitMinutes': 'Please wait {{minutes}} minutes before trying again.',
    'auth.pleaseWaitSeconds': 'Please wait {{seconds}} seconds before trying again.',
    'auth.remainingAttempts': '{{count}} attempts remaining today',
  },
  'zh-TW': {
    'filter': '篩選',
    'clear': '清除',
    'easyApply': '申請容易',
    'flexibleContract': '合約彈性',
    'reviews.search': '搜尋',
    'reviews.searchAddress': '請輸入地點',
    'bedrooms': '臥室',
    'bathrooms': '浴室',
    'singleReview': '單一評價',
    'reviews.lastReview': '最新評價',
    'form.landlordName': '房東或社區名稱',
    'rentalStatus.viewed': '已看房',
    'rentalStatus.renting': '已承租',
    'rentalStatus.ended': '已結束租約',
    'gender': '性別',
    'nationality': '國籍',
    'reviews.months': '個月',
    'reviews.noticePeriod': '通知期',
    'reviews.foundAddresses': '找到 {{count}} 個地址（共 {{reviews}} 則評價）',
    'reviews.viewAsList': '列表顯示',
    'latestReview': '最新評價',
    // Parking translations
    'parking.paid_indoor': '室內付費停車',
    'parking.free_indoor': '室內免費停車',
    'parking.paid_outdoor': '戶外付費停車',
    'parking.free_outdoor': '戶外免費停車',
    'parking.street_parking': '路邊停車',
    'parking.garage': '車庫',
    'parking.driveway': '車道',
    // Authentication and verification
    'auth.forgotPassword': '忘記密碼',
    'auth.forgotPasswordDesc': '請輸入您的電子郵件地址，我們將向您發送重設密碼的連結。',
    'auth.passwordResetEmailSent': '重設密碼郵件已發送',
    'auth.checkEmailForPasswordReset': '請檢查您的電子郵件以取得重設密碼連結。',
    'auth.sendResetLink': '發送重設連結',
    'auth.sending': '發送中...',
    'auth.invalidEmail': '無效的電子郵件',
    'auth.pleaseEnterValidEmail': '請輸入有效的電子郵件地址。',
    'auth.passwordResetFailed': '密碼重設失敗',
    'auth.resetYourPassword': '重設您的密碼',
    'auth.enterNewPassword': '請為您的帳號輸入新密碼。',
    'auth.newPassword': '新密碼',
    'auth.confirmPassword': '確認密碼',
    'auth.passwordResetSuccess': '密碼重設成功',
    'auth.passwordResetSuccessDesc': '您的密碼已成功重設。',
    'auth.passwordTooShort': '密碼長度至少需要6個字符。',
    'auth.passwordsDontMatch': '密碼不匹配。',
    'auth.resetPassword': '重設密碼',
    'auth.updating': '更新中...',
    'auth.invalidOrExpiredResetLink': '無效或已過期的重設連結。',
    'auth.requestNewResetLinkDesc': '請重新申請密碼重設連結。',
    'auth.backToLogin': '返回登入',
    'auth.verifyingResetLink': '正在驗證重設連結...',
    'auth.emailNotVerified': '您的電子郵件尚未驗證。請檢查您的收件匣以獲取驗證郵件。',
    'auth.resendVerificationEmail': '重新發送驗證郵件',
    'auth.verificationEmailSent': '驗證郵件已發送',
    'auth.checkEmailForVerification': '請檢查您的電子郵件以獲取驗證連結。',
    'auth.verificationEmailFailed': '發送驗證郵件失敗',
    'auth.checkYourEmail': '請檢查您的電子郵件',
    'auth.verificationEmailSentDesc': '我們已向您的收件匣發送了一封驗證郵件。請點擊郵件中的連結以驗證您的帳號。',
    'auth.didntReceiveEmail': '沒有收到郵件？',
    'auth.completeProfile': '完成個人資料',
    'auth.pleaseFillProfile': '請填寫您的資訊以完成註冊',
    'auth.emailVerifyFailed': '電子郵件驗證失敗',
    'auth.email': '電子郵件',
    'auth.enterYourEmail': '請輸入您的電子郵件地址',
    // Rate limiting translations
    'auth.dailyLimitReached': '您已達到今日郵件發送上限。請明天再試。',
    'auth.pleaseWaitMinutes': '請等待 {{minutes}} 分鐘後再試。',
    'auth.pleaseWaitSeconds': '請等待 {{seconds}} 秒後再試。',
    'auth.remainingAttempts': '今日剩餘 {{count}} 次嘗試機會',
  }
};

// Translate parking information function
export const translateParkingInfo = (info: string, language: string): string => {
  const lowerInfo = info.toLowerCase();
  
  if (language === 'zh-TW') {
    if (lowerInfo.includes('paid_indoor') || lowerInfo === 'paid_indoor') return '室內付費停車';
    if (lowerInfo.includes('free_indoor') || lowerInfo === 'free_indoor') return '室內免費停車';
    if (lowerInfo.includes('paid_outdoor') || lowerInfo === 'paid_outdoor') return '戶外付費停車';
    if (lowerInfo.includes('free_outdoor') || lowerInfo === 'free_outdoor') return '戶外免費停車';
    if (lowerInfo.includes('street_parking') || lowerInfo === 'street_parking') return '路邊停車';
    if (lowerInfo.includes('garage') || lowerInfo === 'garage') return '車庫';
    if (lowerInfo.includes('driveway') || lowerInfo === 'driveway') return '車道';
  } else {
    if (lowerInfo.includes('paid_indoor') || lowerInfo === 'paid_indoor') return 'Paid Indoor Parking';
    if (lowerInfo.includes('free_indoor') || lowerInfo === 'free_indoor') return 'Free Indoor Parking';
    if (lowerInfo.includes('paid_outdoor') || lowerInfo === 'paid_outdoor') return 'Paid Outdoor Parking';
    if (lowerInfo.includes('free_outdoor') || lowerInfo === 'free_outdoor') return 'Free Outdoor Parking';
    if (lowerInfo.includes('street_parking') || lowerInfo === 'street_parking') return 'Street Parking';
    if (lowerInfo.includes('garage') || lowerInfo === 'garage') return 'Garage';
    if (lowerInfo.includes('driveway') || lowerInfo === 'driveway') return 'Driveway';
  }
  
  return info; // Return original if no translation found
};

export const getTranslation = (
  language: SupportedLanguage,
  key: string,
  options?: { [key: string]: any }
): string => {
  // Get the appropriate translation module based on language
  let translationModule;
  if (language === 'zh-TW') translationModule = zhTW;
  else translationModule = en;
  
  // First check in the imported module
  let translatedValue = getNestedValue(translationModule, key);
  
  // If not found in the imported module, try custom translations
  if (!translatedValue && customTranslations[language] && customTranslations[language][key]) {
    translatedValue = customTranslations[language][key];
  }
  
  if (translatedValue) {
    let text = translatedValue;
    if (options) {
      Object.keys(options).forEach(optionKey => {
        text = text.replace(`{{${optionKey}}}`, options[optionKey]);
      });
    }
    return text;
  }
  return key;
};

export default languageFiles;
